import { Reveal } from "../Reveal";
import { STORY_IMAGES } from "../../lib/content";

export default function Story() {
  return (
    <section
      id="story"
      data-testid="story-section"
      className="relative bg-bone py-24 md:py-40"
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-16">
        {/* Section eyebrow */}
        <Reveal>
          <div className="flex items-center gap-4 mb-12 md:mb-20">
            <span className="overline text-matcha-dark">— Chapter One</span>
            <span className="h-px w-24 bg-ink/30" />
            <span className="font-cormorant italic text-ink-soft">Origin</span>
          </div>
        </Reveal>

        <div className="grid grid-cols-12 gap-6 md:gap-12">
          {/* Headline */}
          <div className="col-span-12 md:col-span-7">
            <Reveal>
              <h2
                className="font-serif font-light text-ink leading-[1.02] tracking-tight"
                style={{ fontSize: "clamp(2.5rem, 6vw, 5.5rem)" }}
              >
                A quieter way
                <br />
                <span className="italic font-cormorant text-matcha-dark">
                  to drink tea.
                </span>
              </h2>
            </Reveal>
          </div>

          {/* Floating image — small, offset */}
          <div className="col-span-12 md:col-span-4 md:col-start-9 md:-mt-8">
            <Reveal delay={0.15}>
              <div className="relative aspect-[4/5] overflow-hidden rounded-sm">
                <img
                  src={STORY_IMAGES.powder}
                  alt="Ceremonial matcha powder"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                />
              </div>
              <p className="font-cormorant italic text-ink-soft text-sm mt-3 text-right">
                — Stone-ground tencha leaf
              </p>
            </Reveal>
          </div>
        </div>

        {/* Body — asymmetric two columns */}
        <div className="grid grid-cols-12 gap-6 md:gap-12 mt-16 md:mt-32">
          <div className="col-span-12 md:col-span-5 md:col-start-2">
            <Reveal>
              <p className="font-cormorant text-2xl md:text-3xl leading-snug text-ink/90">
                Matcha Story began with a single bowl, a granite mill, and the
                patient belief that good things should taste like the place
                they came from.
              </p>
            </Reveal>
          </div>

          <div className="col-span-12 md:col-span-4 md:col-start-8 space-y-6 text-ink-soft text-base leading-relaxed">
            <Reveal delay={0.1}>
              <p>
                We work with Marukyu Koyamaen Main Plant. Their tencha is
                shaded for twenty days beneath bamboo cloth, hand-picked at
                first flush, and milled in our café — slowly — at forty grams
                an hour.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p>
                Everything else follows that one rule: respect the leaf. The
                desserts are simple. The room is calm. The cup, when it
                arrives, is whisked the moment you order.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-2 border-l-2 border-matcha-dark pl-5 py-1">
                <p className="overline text-matcha-dark mb-1.5">
                  Marukyu Koyamaen
                </p>
                <p className="font-cormorant italic text-ink text-lg leading-snug">
                  300 Years of History at Ogura — the heart of Uji, Kyoto.
                </p>
                <p className="font-cormorant italic text-ink-soft text-sm mt-2">
                  &ldquo;Making Teas with Quality as the Highest Priority.&rdquo;
                </p>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Wide image — full width */}
        <Reveal delay={0.1}>
          <div className="relative mt-24 md:mt-32 aspect-[16/7] overflow-hidden rounded-sm">
            <img
              src={STORY_IMAGES.field}
              alt="Tea field at first flush"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            <div className="absolute bottom-6 md:bottom-10 left-6 md:left-12 right-6 md:right-12 flex items-end justify-between text-bone">
              <p className="font-cormorant italic text-lg md:text-2xl max-w-md">
                "The bowl waits. The water settles. The whisk does the rest."
              </p>
              <span className="overline text-bone/80 hidden md:inline">
                — A morning in May
              </span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
